// fragmentThumbnail.js
//
// A small rendered picture of one fragment, for the before/after columns
// in a diff panel.
//
// Why render rather than screenshot the viewer: the thing being compared
// often is not on screen. A deleted element is absent from the current
// model by definition, and the previous generation of a modified one
// only exists in an older export. Neither can be captured from the live
// canvas, so both are drawn here from their own fragment GLB - which is
// downloadable for either generation, because fragments are
// content-addressed and immutable (see back/utils/editFragments.js).
//
// ONE renderer for the whole app, reused for every thumbnail. A browser
// allows only a handful of WebGL contexts (~16 in Chrome) and silently
// drops the oldest when you exceed it - a panel that made a canvas per
// row would blank the real viewer sitting next to it.
import {
  Scene, PerspectiveCamera, WebGLRenderer, Mesh, Box3, Vector3, Color,
  MeshStandardMaterial, HemisphereLight, DirectionalLight,
} from "three";
import { loadFragmentGeometry } from "helpers/editsScene";
export { resolveFragmentUrl } from "helpers/fragmentUrl";

const cache = new Map();        // key -> Promise<dataURL|null>
const MAX_CACHE = 400;

let shared = null;
function renderer(size) {
  if (!shared) {
    const r = new WebGLRenderer({ antialias: true, alpha: true, preserveDrawingBuffer: true });
    r.setPixelRatio(1);            // thumbnails are small; DPR just costs memory
    const scene = new Scene();
    const camera = new PerspectiveCamera(45, 1, 0.01, 100000);
    scene.add(new HemisphereLight(0xffffff, 0x53606a, 1.2));
    const key = new DirectionalLight(0xffffff, 0.8);
    key.position.set(1, 1.6, 1.2);
    scene.add(key);
    shared = { r, scene, camera, size: 0 };
  }
  if (shared.size !== size) {
    shared.r.setSize(size, size, false);
    shared.camera.aspect = 1;
    shared.camera.updateProjectionMatrix();
    shared.size = size;
  }
  return shared;
}

/**
 * @param {string} url        absolute url of the fragment GLB
 * @param {{size?:number, color?:string, background?:string}} opts
 * @returns {Promise<string|null>} a PNG data URL, or null when the
 *          fragment could not be loaded (a missing preview must never
 *          be an error the panel has to handle).
 */
export function renderFragmentThumbnail(url, opts = {}) {
  const { size = 96, color = "#7C8B95", background = null } = opts;
  if (!url) return Promise.resolve(null);
  const key = `${url}|${size}|${color}|${background || "t"}`;
  if (cache.has(key)) return cache.get(key);

  const p = loadFragmentGeometry(url)
    .then(({ geometry }) => {
      const ctx = renderer(size);
      const material = new MeshStandardMaterial({ color: new Color(color), roughness: 0.8, metalness: 0.05 });
      const mesh = new Mesh(geometry, material);
      // The fragment's own local space: thumbnails compare SHAPE, so the
      // world placement is deliberately left off. Two generations of a
      // wall should sit identically in their frames even when one of them
      // moved thirty feet.
      ctx.scene.add(mesh);
      try {
        const box = new Box3().setFromObject(mesh);
        if (box.isEmpty()) return null;
        const sphereSize = box.getSize(new Vector3());
        const center = box.getCenter(new Vector3());
        const radius = Math.max(sphereSize.length() / 2, 1e-4);
        const dist = radius / Math.sin((ctx.camera.fov * Math.PI) / 360);
        ctx.camera.near = Math.max(dist / 1000, 1e-4);
        ctx.camera.far = dist * 20;
        ctx.camera.position.copy(center).add(new Vector3(1, 0.8, 1).normalize().multiplyScalar(dist * 1.35));
        ctx.camera.lookAt(center);
        ctx.camera.updateProjectionMatrix();
        ctx.r.setClearColor(background ? new Color(background) : new Color("#ffffff"), background ? 1 : 0);
        ctx.r.render(ctx.scene, ctx.camera);
        return ctx.r.domElement.toDataURL("image/png");
      } finally {
        // Always detach: a mesh left in the shared scene appears behind
        // every later thumbnail.
        ctx.scene.remove(mesh);
        material.dispose();
      }
    })
    .catch(() => null);

  cache.set(key, p);
  if (cache.size > MAX_CACHE) cache.delete(cache.keys().next().value);
  return p;
}

export function clearThumbnailCache() { cache.clear(); }
